import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import dayjs from 'dayjs';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { SortEvent } from 'primeng/api';
import { TableModule } from 'primeng/table';
import { Textarea } from 'primeng/textarea';
import { Checkbox } from 'primeng/checkbox';
import { Button } from 'primeng/button';
import { SvgIconComponent } from 'angular-svg-icon';
import { DayJSDateFormat } from '@app/core/enums/date-format.enum';
import { DayjsPipe } from '@app/shared/pipes/dayjs/dayjs.pipe';
import { FormattingDataService } from '@app/core/services/formatting-data/formatting-data.service';
import { GridLinesDatatableToken } from 'src/theme/components/datatable/grid-lines-datatable-token';
import { IconBadgeComponent } from '@app/shared/components/icon-badge/icon-badge.component';
import { IEventSnapshot } from '@app/api/interfaces/event.interface';
import { ldEventStatusLocalize } from '@app/main/leak-detection/localize/ld-event-status';
import {
  deriveLdEventInvestigationResult,
  deriveLdEventRepairStatus,
} from '@app/main/leak-detection/utils/ld-event-status';
import { getLdEventStatusBackground, getLdEventStatusIcon } from '@app/main/leak-detection/utils/ld-event-status-icon';
import { dialogEventResultLabel } from '@app/main/leak-detection/dialogs/dialog-event/configs/dialog-event-badge.config';
import { DIALOG_EVENT_MAX_COMMENT_LENGTH } from '@app/main/leak-detection/dialogs/dialog-event/configs/dialog-event-comment.config';
import { DialogEventState } from '@app/main/leak-detection/dialogs/dialog-event/services/dialog-event-state.service';
import { DialogEventHistory } from '@app/main/leak-detection/dialogs/dialog-event/services/dialog-event-history.service';

interface IHistoryRow {
  id: string;
  time: number;
  performedAt: string;
  iconSrc: string;
  iconBackground: string;
  statusLabel: string;
  outcomeLabel: string | null;
  performedBy: string | null;
  action: string;
  comment: string | null;
}

@Component({
  selector: 'gc-dialog-event-history',
  standalone: true,
  imports: [
    NgClass,
    NgTemplateOutlet,
    ReactiveFormsModule,
    TranslateDirective,
    TranslatePipe,
    DayjsPipe,
    TableModule,
    Textarea,
    Checkbox,
    Button,
    SvgIconComponent,
    IconBadgeComponent,
  ],
  templateUrl: './dialog-event-history.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex flex-col h-full min-h-0' },
})
export class DialogEventHistoryComponent {
  private readonly fb = inject(FormBuilder);
  private readonly dialogState = inject(DialogEventState);
  private readonly formatting = inject(FormattingDataService);

  protected readonly history = inject(DialogEventHistory);

  protected readonly gridLinesDatatableToken = GridLinesDatatableToken;
  protected readonly dateFormat = DayJSDateFormat.YYYYMMDD;
  protected readonly maxCommentLength = DIALOG_EVENT_MAX_COMMENT_LENGTH;
  protected readonly emptyValue = this.formatting.NO_DATA_VALUE_PLACEHOLDER_KEY;

  /** newest first */
  protected readonly sortOrder = signal(-1);

  protected readonly rows = computed<IHistoryRow[]>(() =>
    this.history.visibleHistory().map(entry => {
      const { status } = entry.snapshot;

      return {
        id: entry.id,
        time: dayjs(entry.performedAt).valueOf(),
        performedAt: entry.performedAt,
        iconSrc: getLdEventStatusIcon(status),
        iconBackground: getLdEventStatusBackground(status),
        statusLabel: ldEventStatusLocalize[status],
        outcomeLabel: outcomeLabel(entry.snapshot),
        performedBy: entry.performedBy ?? null,
        action: entry.message,
        comment: entry.comment?.trim() || null,
      };
    })
  );

  protected readonly commentForm = this.fb.nonNullable.group({
    comment: ['', Validators.maxLength(DIALOG_EVENT_MAX_COMMENT_LENGTH)],
  });

  protected readonly isFocused = signal(false);

  protected onSortChange(event: SortEvent): void {
    if (event.order != null) this.sortOrder.set(event.order);
  }

  protected cancelComment(): void {
    this.commentForm.reset();
  }

  protected submitComment(): void {
    if (this.commentForm.invalid) return;

    this.dialogState.addComment(this.commentForm.controls.comment.value);
    this.commentForm.reset();
  }
}

function outcomeLabel(snapshot: IEventSnapshot): string | null {
  const investigationResult = deriveLdEventInvestigationResult(snapshot);

  return investigationResult ? dialogEventResultLabel(investigationResult, deriveLdEventRepairStatus(snapshot)) : null;
}
